import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup,  Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatSnackBar } from '@angular/material';
import { RunwayService } from '../service/runway.service';
import { EdgeLighting, LandingSurface, LandingSurfaceCondition, LandingSurfaceType, PavementType } from '../models/landing-surface.model';
import { AirportNavaid } from '../models/airport-navaid.model';

@Component({
  selector: 'app-edit-runway',
  templateUrl: './edit-runway.component.html',
  styleUrls: ['./edit-runway.component.scss']
})
export class EditRunwayComponent implements OnInit {
  runway: LandingSurface;
  origRunway: LandingSurface;
  isNew = false;
  isLoading = true;
  airportId: number;
  formGroup: FormGroup;
  selectedRunwayType: string;
  selectedCondition: string;
  selectedPavementType: string;
  selectedEdgeLighting: string;

  constructor(private _matDialogRef: MatDialogRef<EditRunwayComponent>,
              public _snackBar: MatSnackBar,
              @Inject(MAT_DIALOG_DATA) data,
              private _runwayService: RunwayService) {
    this.runway = data.runway || new LandingSurface();
    this.isNew = data.isNew || false;
    this.airportId = data.airID;
    this.origRunway = Object.assign(new LandingSurface, data.runway);

    if (this.isNew) {
      this.selectedRunwayType = '';
      this.selectedCondition = '';
      this.selectedPavementType = '';
      this.selectedEdgeLighting = '';
    } else {
      this.selectedRunwayType = this.getRunwayType(this.runway.type);
      this.selectedCondition = this.getRunwayCondition(this.runway.condition);
      this.selectedPavementType = this.getPavementType(this.runway.pavementType);
      this.selectedEdgeLighting = this.getEdgeLighting(this.runway.edgeLighting);
    }
  }

  ngOnInit() {
    this._matDialogRef.backdropClick().subscribe(() => {
      this.close({cancelBtnClicked: true});
    });
    this.formGroup = new FormGroup({
      name: new FormControl(this.runway.name, [Validators.required, Validators.maxLength(100)]),
      length: new FormControl(this.runway.length, [Validators.required, Validators.pattern('^[0-9]*$'), Validators.maxLength(8)]),
      width: new FormControl(this.runway.length, [Validators.required, Validators.pattern('^[0-9]*$'), Validators.maxLength(8)]),
      type: new FormControl(this.selectedRunwayType),
      materialType1: new FormControl(this.runway.materialType1, [Validators.required, Validators.maxLength(30)]),
      materialType2: new FormControl(this.runway.materialType2, [Validators.maxLength(30)]),
      treatment: new FormControl(this.runway.treatment, [Validators.maxLength(30)]),
      condition: new FormControl(this.selectedCondition, [Validators.required]),
      pavementType: new FormControl(this.selectedPavementType),
      edgeLighting: new FormControl(this.selectedEdgeLighting),
      pcn: new FormControl(this.runway.pcn, [Validators.min(0), Validators.max(999)]),
      subgradeStrength: new FormControl(this.runway.subgradeStrength),
      tirePressure: new FormControl(this.runway.tirePressure),
      wtBearing1Wheel: new FormControl(this.runway.wtBearing1Wheel, [Validators.min(0), Validators.max(9999)]),
      wtBearing2Wheel: new FormControl(this.runway.wtBearing2Wheel, [Validators.min(0), Validators.max(9999)]),
      wtBearing2WheelDblTandem: new FormControl(this.runway.wtBearing2WheelDblTandem, [Validators.min(0), Validators.max(9999)]),
      wtBearing2WheelTandem: new FormControl(this.runway.wtBearing2WheelTandem, [Validators.min(0), Validators.max(9999)])
    });
    this.isLoading = false;
  }

  getRunwayType(type: LandingSurfaceType): string {
    return LandingSurface.formatLandingSurfaceType(type);
  }

  setRunwayType(type: string): LandingSurfaceType {
    return LandingSurface.parseLandingSurfaceType(type);
  }

  getRunwayCondition(condition: LandingSurfaceCondition): string {
    return LandingSurface.formatCondition(condition);
  }

  setRunwayCondition(condition: string): LandingSurfaceCondition {
    return LandingSurface.parseCondition(condition);
  }

  getPavementType(pavementType: PavementType): string {
    return LandingSurface.formatPavementType(pavementType);
  }

  setPavementType(pavementType: string): PavementType {
    return LandingSurface.parsePavementType(pavementType);
  }

  getEdgeLighting(edgeLighting: EdgeLighting): string {
    return LandingSurface.formatEdgeLighting(edgeLighting);
  }

  setEdgeLighting(edgeLighting: string): EdgeLighting {
    return LandingSurface.parseEdgeLighting(edgeLighting);
  }

  /**
   * triggered by save button click event
   * @return {Promise<void>}
   */
  async save() {
    try {
      let result: any;
      this.runway.name = this.formGroup.value.name;
      this.runway.length = this.formGroup.value.length;
      this.runway.width = this.formGroup.value.width;
      this.runway.type = this.setRunwayType(this.formGroup.value.type);
      this.runway.materialType1 = this.formGroup.value.materialType1;
      this.runway.materialType2 = this.formGroup.value.materialType2;
      this.runway.treatment = this.formGroup.value.treatment;
      this.runway.condition = this.setRunwayCondition(this.formGroup.value.condition);
      this.runway.pavementType = this.setPavementType(this.formGroup.value.pavementType);
      this.runway.edgeLighting = this.setEdgeLighting(this.formGroup.value.edgeLighting);
      this.runway.pcn = this.formGroup.value.pcn;
      this.runway.subgradeStrength = this.formGroup.value.subgradeStrength;
      this.runway.tirePressure = this.formGroup.value.tirePressure;
      this.runway.wtBearing1Wheel = this.formGroup.value.wtBearing1Wheel;
      this.runway.wtBearing2Wheel = this.formGroup.value.wtBearing2Wheel;
      this.runway.wtBearing2WheelTandem = this.formGroup.value.wtBearing2WheelTandem;
      this.runway.wtBearing2WheelDblTandem = this.formGroup.value.wtBearing2WheelDblTandem;

      if (this.isNew) {
        result = await this._runwayService.postRunway(this.airportId, this.runway);
        this.runway = LandingSurface.parse(result);
      } else {
        result = await this._runwayService.putRunway(this.airportId, this.runway);
        this.runway = LandingSurface.parse(result);
      }
      this.close({cancelBtnClicked: false, success: true, runway: this.runway});
    } catch (e) {
      this.close({cancelBtnClicked: false, success: false, error: e});
    }
  }

  /**
   * triggered by cancel button click event
   */
  cancel () {
    this.close({cancelBtnClicked: true});
  }

  /**
   * closes modal
   * @param data
   */
  close(data: any) {
    if (data && data.cancelBtnClicked) {
      Object.assign(this.runway, this.origRunway);
    }
    data['isNew'] = this.isNew;
    this._matDialogRef.close(data);
  }
}
